import React, { useState } from 'react';
import { getContrastText } from '../../utils/colorUtils';

interface CompanyLogoProps {
  src?: string | null;
  companyName?: string;
  primaryColor?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  rounded?: 'full' | 'lg' | 'xl' | '2xl' | 'none';
  /** Skip the neutral fill behind square logos (for placements already on white). */
  plain?: boolean;
}

// Per-size styling. `container` is used for the square chip layout (icon-style
// logos and initials fallback). `wide` is used when the logo is detected to be
// a landscape/full logo and should render as a height-bounded banner.
const sizeMap = {
  xs: {
    container: 'w-8 h-8',
    wide: 'h-6 sm:h-7 max-w-[110px] sm:max-w-[130px]',
    text: 'text-xs',
    icon: 'text-2xs',
  },
  sm: {
    container: 'w-10 h-10',
    wide: 'h-8 sm:h-10 max-w-[140px] sm:max-w-[180px]',
    text: 'text-sm',
    icon: 'text-xs',
  },
  md: {
    container: 'w-12 h-12',
    wide: 'h-10 sm:h-12 max-w-[180px] sm:max-w-[220px]',
    text: 'text-base',
    icon: 'text-sm',
  },
  lg: {
    container: 'w-16 h-16',
    wide: 'h-12 sm:h-14 max-w-[220px] sm:max-w-[280px]',
    text: 'text-xl',
    icon: 'text-base',
  },
  xl: {
    container: 'w-20 h-20 sm:w-24 sm:h-24',
    wide: 'h-14 sm:h-16 md:h-20 max-w-[240px] sm:max-w-[320px] md:max-w-[380px]',
    text: 'text-3xl sm:text-4xl',
    icon: 'text-xl',
  },
};

const roundedMap = {
  full: 'rounded-full',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  none: '',
};

// Logos with a width:height ratio at/above this threshold are treated as
// "full" / landscape logos and rendered as a banner instead of being squashed
// into a square chip. ~1.3 catches typical horizontal wordmarks while still
// letting near-square emblem logos use the chip look.
const WIDE_ASPECT_RATIO = 1.3;

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function hashStringToHue(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

const CompanyLogo: React.FC<CompanyLogoProps> = ({
  src,
  companyName = '',
  primaryColor,
  size = 'md',
  className = '',
  rounded = 'xl',
  plain = false,
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  const showImage = src && !imgFailed;
  const s = sizeMap[size];
  const r = roundedMap[rounded];

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      const ratio = img.naturalWidth / img.naturalHeight;
      setAspectRatio((prev) => (prev === ratio ? prev : ratio));
    }
  };

  if (showImage) {
    const isWide = aspectRatio !== null && aspectRatio >= WIDE_ASPECT_RATIO;

    if (isWide) {
      // Banner layout for landscape/full logos: scale by height with a
      // responsive max width and no chrome around the artwork.
      return (
        <img
          src={src}
          alt={companyName || 'Company logo'}
          className={`${s.wide} w-auto object-contain flex-shrink-0 ${className}`}
          onError={() => setImgFailed(true)}
          onLoad={handleImageLoad}
        />
      );
    }

    // Square chip layout for icon-style logos. The subtle neutral fill keeps
    // transparent PNGs readable on any background.
    return (
      <div
        className={`${s.container} ${r} overflow-hidden flex-shrink-0 flex items-center justify-center ${plain ? '' : 'bg-ink-50'} ${className}`}
      >
        <img
          src={src}
          alt={companyName || 'Company logo'}
          className="w-full h-full object-contain"
          onError={() => setImgFailed(true)}
          onLoad={handleImageLoad}
        />
      </div>
    );
  }

  const initials = companyName ? getInitials(companyName) : '?';

  let bgFrom: string;
  let bgTo: string;
  let textColor: string;

  if (primaryColor && primaryColor !== '#0ea5e9') {
    bgFrom = primaryColor;
    bgTo = primaryColor;
    textColor = getContrastText(primaryColor) === 'white' ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.8)';
  } else {
    const hue = hashStringToHue(companyName || 'default');
    bgFrom = `hsl(${hue}, 55%, 50%)`;
    bgTo = `hsl(${(hue + 25) % 360}, 60%, 42%)`;
    textColor = 'rgba(255,255,255,0.95)';
  }

  return (
    <div
      className={`${s.container} ${r} flex-shrink-0 flex items-center justify-center select-none ${className}`}
      style={{
        background: `linear-gradient(135deg, ${bgFrom}, ${bgTo})`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.15)',
      }}
    >
      <span
        className={`${s.text} font-bold leading-none tracking-tight`}
        style={{ color: textColor }}
      >
        {initials}
      </span>
    </div>
  );
};

export default CompanyLogo;
